import * as React from 'react'

import { InfoIcon } from 'lucide-react'
import { isMacOs } from 'react-device-detect'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Kbd, KbdGroup } from '~/client/src/shared/components/shadcn-ui/kbd'
import {
  Popover,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import {
  Tooltip,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  MarkupPopoverContent as PopoverContent,
  MarkupTooltipContent as TooltipContent,
} from './MarkupPortalContainer'

interface IProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  showHint: boolean
  showLocationTool?: boolean
  // Host-specific groups listed before the editor's own (e.g. the Workflows
  // map's create gesture); the legend itself stays host-agnostic.
  extraGroups?: IShortcutGroup[]
  // Center the legend over the toolbar, not its right-edge trigger.
  anchorRef?: React.RefObject<HTMLElement | null>
}

export interface IShortcutRow {
  label: string
  keys: string[]
}

export interface IShortcutGroup {
  title: string
  rows: IShortcutRow[]
}

const MOD = isMacOs ? '⌘' : 'Ctrl'

const buildShortcutGroups = (
  showLocationTool: boolean,
  extraGroups: IShortcutGroup[],
): IShortcutGroup[] => [
  ...extraGroups,
  {
    title: 'Tools',
    rows: [
      { label: 'Select / deselect', keys: ['Esc'] },
      { label: 'Rectangle', keys: ['R'] },
      { label: 'Circle', keys: ['O'] },
      { label: 'Line', keys: ['L'] },
      { label: 'Arrow', keys: ['⇧', 'L'] },
      { label: 'Polygon', keys: ['P'] },
      { label: 'Polyline', keys: ['⇧', 'P'] },
      { label: 'Text', keys: ['T'] },
      { label: 'Draw', keys: ['D'] },
      ...(showLocationTool
        ? [{ label: 'Move Pin Position', keys: ['M'] }]
        : []),
    ],
  },
  ...(showLocationTool
    ? [
        {
          title: 'Move Pin Position',
          rows: [
            { label: 'Confirm move', keys: ['Enter'] },
            { label: 'Cancel move', keys: ['Esc'] },
          ],
        },
      ]
    : []),
  {
    title: 'Edit',
    rows: [
      { label: 'Undo', keys: [MOD, 'Z'] },
      { label: 'Redo', keys: ['⇧', MOD, 'Z'] },
      { label: 'Copy', keys: [MOD, 'C'] },
      { label: 'Paste', keys: [MOD, 'V'] },
      { label: 'Delete', keys: ['Del'] },
    ],
  },
  {
    title: 'Text box',
    rows: [
      { label: 'Commit / new box', keys: ['Enter'] },
      { label: 'Line break', keys: ['⇧', 'Enter'] },
      { label: 'Cancel', keys: ['Esc'] },
    ],
  },
  {
    title: 'Drawing (hold while acting)',
    rows: [
      { label: 'Snap line to 45°', keys: ['⇧'] },
      { label: 'Snap rotation to 15°', keys: ['⇧'] },
    ],
  },
  {
    title: 'Vertices (line / polygon)',
    rows: [
      { label: 'Edit vertices', keys: ['Click again'] },
      { label: 'Move vertex', keys: ['Drag'] },
      { label: 'Insert vertex', keys: ['Click midpoint'] },
      { label: 'Delete vertex', keys: ['Right-click'] },
      { label: 'Finish shape', keys: ['Enter'] },
      { label: 'Cancel shape', keys: ['Esc'] },
    ],
  },
  {
    title: 'Map (drag to apply)',
    rows: [
      { label: 'Pan', keys: ['drag'] },
      { label: 'Tilt & rotate', keys: ['Ctrl', 'drag'] },
      { label: 'Zoom to area', keys: ['⇧', 'drag'] },
    ],
  },
  {
    title: 'Help',
    rows: [{ label: 'Show / hide shortcuts', keys: ['I'] }],
  },
]

// On the dark legend surface the keycaps need the same translucent-light
// treatment the tooltip surface gives them (the kbd auto-rule only keys off
// tooltip-content, not popover-content).
const KEY_CLASS = 'tw:bg-background/20 tw:text-background'

// Help affordance for the markup editor: a keyboard button that toggles a dark
// legend card (Popover styled like the toolbar tooltips) listing every shortcut,
// plus a first-time "Press I for shortcuts" hint. The editor owns open/hint
// state (and the `I` key); this is the presentation.
const MarkupShortcutsLegend: React.FC<IProps> = ({
  open,
  onOpenChange,
  showHint,
  showLocationTool,
  extraGroups,
  anchorRef,
}) => {
  const groups = buildShortcutGroups(!!showLocationTool, extraGroups ?? [])
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <div className="tw:relative tw:inline-flex">
        <Tooltip>
          <PopoverTrigger
            render={
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={Localization.translator.shortcuts}
                    aria-pressed={open}
                    className={
                      open
                        ? 'tw:bg-primary/8 tw:text-primary tw:hover:bg-primary/8'
                        : 'tw:text-foreground'
                    }
                  />
                }
              />
            }
          >
            <InfoIcon aria-hidden="true" />
          </PopoverTrigger>
          <TooltipContent side="top">
            Shortcuts <Kbd>I</Kbd>
          </TooltipContent>
        </Tooltip>
        {/* First-entry nudge. A self-rendered chip (not a base-ui tooltip) — the
          shared TooltipProvider gates tooltips on hover/focus, so a controlled
          one can't reliably auto-open. Styled to match the dark tooltip surface. */}
        {showHint && (
          <div className="tw:pointer-events-none tw:absolute tw:right-0 tw:bottom-full tw:mb-2 tw:flex tw:w-max tw:items-center tw:gap-1.5 tw:rounded-md tw:bg-foreground tw:px-3 tw:py-1.5 tw:text-xs tw:text-background tw:shadow-md">
            Press <Kbd className={KEY_CLASS}>I</Kbd> for shortcuts
          </div>
        )}
      </div>
      <PopoverContent
        side="top"
        align="center"
        anchor={anchorRef}
        className="tw:grid tw:w-[36rem] tw:grid-cols-3 tw:gap-x-5 tw:gap-y-3 tw:bg-foreground tw:text-background"
      >
        {groups.map(group => (
          <div key={group.title} className="tw:flex tw:flex-col tw:gap-1">
            <div className="tw:text-label-sm tw:text-background/60 tw:uppercase">
              {group.title}
            </div>
            {group.rows.map(row => (
              <div
                key={row.label}
                className="tw:flex tw:items-center tw:justify-between tw:gap-2"
              >
                <span className="tw:text-background/90">{row.label}</span>
                <KbdGroup className="tw:shrink-0">
                  {row.keys.map((key, i) => (
                    <Kbd key={i} className={KEY_CLASS}>
                      {key}
                    </Kbd>
                  ))}
                </KbdGroup>
              </div>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  )
}

export default MarkupShortcutsLegend
